import { AnyAction, combineReducers, configureStore, createAction, ThunkAction } from '@reduxjs/toolkit'
import vendorReducer from './vendors/vendorSlice'
import userReducer from './user/userSlice'
import rssReducer from './rss/rssSlice'
import securityFindingReducer from './security-findings/securityFindingSlice'
import vulnerabilityReducer from './vulnerabilities/vulnerabilitySlice'
import usCertReducer from './us-certs/usCertSlice'
import modalReducer from './modals/modalSlice'
import notificationReducer from './notifications/notificationSlice'
import integrationsReducer from './integrations/integrationsSlice'
import jiraReducer from './jira/jiraSlice'
import drawerReducer from './drawers/drawerSlice'
import toastReducer from './toasts/toastSlice'
import trendsReducer from './trends/trendsSlice'
import dashboardReducer from './dashboard/dashboardSlice'
import themeReducer from './theme/themeSlice'

const combinedReducer = combineReducers({
  vendors: vendorReducer,
  user: userReducer,
  securityFindings: securityFindingReducer,
  vulnerabilities: vulnerabilityReducer,
  usCerts: usCertReducer,
  rss: rssReducer,
  modals: modalReducer,
  drawers: drawerReducer,
  notifications: notificationReducer,
  integrations: integrationsReducer,
  jira: jiraReducer,
  toasts: toastReducer,
  trends: trendsReducer,
  dashboard: dashboardReducer,
  theme: themeReducer
})

export type RootState = ReturnType<typeof combinedReducer>

/* Fired when the app should forget the current account. Handled at the top of the store so every
 * account-related slice returns to its starting state at once — add a slice later and it's cleared
 * too, with no reset code of its own. */
export const resetAccountState = createAction('session/resetAccountState')

/* Only the theme (a user's own preference) carries across an account switch. Everything else is
 * cleared, including any open modal or drawer, which may still hold data from the account being left. */
const PRESERVED_SLICES: (keyof RootState)[] = ['theme']

const rootReducer = (state: RootState | undefined, action: AnyAction): RootState => {
  if (action.type === resetAccountState.type && state) {
    const preserved = PRESERVED_SLICES.reduce((acc, key) => ({ ...acc, [key]: state[key] }), {} as Partial<RootState>)
    return combinedReducer(preserved as RootState, action)
  }
  return combinedReducer(state, action)
}

export const store = configureStore({
  reducer: rootReducer
})

export type AppDispatch = typeof store.dispatch

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, AnyAction>
